import React, { useState, useEffect } from "react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";
import { Link, useLocation } from "react-router-dom";
import { BsMenuButton } from "react-icons/bs";
import { FaCheckCircle } from "react-icons/fa";
import {
  getOrderDetail,
  requestWaiter,
  getWaiterRequest,
} from "../../../api/user/restaurants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import socket from "../../../helper/socket";
import toast from "react-hot-toast";
import { IoMdEye } from "react-icons/io";
import UserImage from "../../../helper/images/customer.png";
import { BiSolidBot } from "react-icons/bi";
import Chat from "../../Chat";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] sm:max-w-[90%] sm:w-[579px] w-full relative sm:shadow-lg py-[30px] px-[54px]">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const PopupModal2 = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const TrackOrder = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);
  const [tableNo, setTableNo] = useState("");
  const location = useLocation();
  const { orderId } = location.state || {};
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["order-confirmation", orderId],
    queryFn: () => getOrderDetail(orderId),
    enabled: !!orderId,
    keepPreviousData: true,
  });

  const order = data?.data?.order;
  const reservationId = order?.reservationId?._id;

  const { data: waiterRequest } = useQuery({
    queryKey: ["waiter-request", reservationId],
    queryFn: () => getWaiterRequest(reservationId),
    enabled: !!reservationId,
    keepPreviousData: true,
  });

  const reservation = waiterRequest?.data?.reservation;

  const requestWaiterMutation = useMutation({
    mutationFn: requestWaiter,
    onSuccess: () => {
      toast.success(t("trackOrder").requestSent);
      queryClient.invalidateQueries("waiter-request");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || t("trackOrder").requestSent;
      toast.error(errorMessage);
    },
  });

  const getStepStatus = (currentStatus, stepStatus) => {
    const steps = ["confirmed", "preparing", "ready", "served"];
    return steps.indexOf(currentStatus) >= steps.indexOf(stepStatus);
  };

  useEffect(() => {
    const handleStatusUpdate = () => {
      refetch();
    };

    socket.on("statusUpdated", handleStatusUpdate);

    return () => {
      socket.off("statusUpdated", handleStatusUpdate);
    };
  }, [refetch]);

  useEffect(() => {
    if (order?.reservationId?.tableNo) {
      setTableNo(order.reservationId.tableNo);
    }
  }, [order]);

  const handleWaiterRequest = () => {
    if (!tableNo.trim()) {
      return toast.error(t("trackOrder").enterTableError);
    }
    // If we have a reservationId, include it, otherwise just send tableNo
    const requestData = order?.reservationId?._id
      ? { reservationId: order.reservationId._id, tableNo }
      : { tableNo };
    requestWaiterMutation.mutate(requestData);
  };

  return (
    <>
      <div className="lg:py-[60px] sm:py-[40px] py-[30px]">
        <div className="container">
          <div className="w-full mx-auto max-w-[1030px] md:py-[41px] md:px-[37px] p-[20px] border-[#DDDDDD] border rounded-[10px]">
            <div>
              {/* Step 1 */}
              <div className="pl-[44px] relative pb-[35px]">
                <div
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "0px",
                    height: "100%",
                    borderLeft: getStepStatus(order?.orderStatus, "confirmed")
                      ? "3px solid #4CAF50"
                      : "3px solid #DDDDDD",
                    zIndex: 0,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "0px",
                    height: "19px",
                    width: "19px",
                    borderRadius: "50%",
                    backgroundColor: "#4CAF50",
                    zIndex: 1,
                  }}
                ></div>
                <div>
                  <h4 className="text-[#18181B] text-[22px] font-semibold leading-[32px] mb-2">
                    {t("trackOrder").orderConfirmed}
                  </h4>
                  <p className="text-lg text-[#18181B] leading-[28px]">
                    {t("trackOrder").orderConfirmedMessage}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="pl-[44px] relative pb-[35px]">
                <div
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "0px",
                    height: "100%",
                    borderLeft: getStepStatus(order?.orderStatus, "preparing")
                      ? "3px solid #4CAF50"
                      : "3px solid #DDDDDD",
                    zIndex: 0,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "0px",
                    height: "19px",
                    width: "19px",
                    borderRadius: "50%",
                    backgroundColor: getStepStatus(
                      order?.orderStatus,
                      "preparing"
                    )
                      ? "#4CAF50"
                      : "#ccc",
                    zIndex: 1,
                  }}
                ></div>
                <div>
                  <h4 className="text-[#18181B] text-[22px] font-semibold leading-[32px] mb-2">
                    {t("trackOrder").preparing}
                  </h4>
                  <p className="text-lg text-[#18181B] leading-[28px]">
                    {t("trackOrder").preparingMessage}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="pl-[44px] relative pb-[35px]">
                <div
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "0px",
                    height: "100%",
                    borderLeft: getStepStatus(order?.orderStatus, "ready")
                      ? "3px solid #4CAF50"
                      : "3px solid #DDDDDD",
                    zIndex: 0,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "0px",
                    height: "19px",
                    width: "19px",
                    borderRadius: "50%",
                    backgroundColor: getStepStatus(order?.orderStatus, "ready")
                      ? "#4CAF50"
                      : "#ccc",
                    zIndex: 1,
                  }}
                ></div>
                <div>
                  <h4 className="text-[#18181B] text-[22px] font-semibold leading-[32px] mb-2">
                    {t("trackOrder").readyToServe}
                  </h4>
                  <p className="text-lg text-[#18181B] leading-[28px]">
                    {t("trackOrder").readyToServeMessage}
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="pl-[44px] relative pb-[35px]">
                <div
                  style={{
                    position: "absolute",
                    left: "0",
                    top: "0px",
                    height: "19px",
                    width: "19px",
                    borderRadius: "50%",
                    backgroundColor: getStepStatus(order?.orderStatus, "served")
                      ? "#4CAF50"
                      : "#ccc",
                    zIndex: 1,
                  }}
                ></div>
                <div>
                  <h4 className="text-[#18181B] text-[22px] font-semibold leading-[32px] mb-2">
                    {t("trackOrder").served}
                  </h4>
                  <p className="text-lg text-[#18181B] leading-[28px]">
                    {t("trackOrder").servedMessage}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-[30px] items-center text-center mt-[30px] flex-wrap">
            {/* <button
              onClick={()=> setIsChatPopupOpen(true)}
              className="inline-block w-max rounded-[2px] bg-[#FFA808] 2xl:text-[20px] text-base text-white text-center font-bold 2xl:p-[14px] 2xl:px-[18px] p-3 yellow-shadow hover:bg-[#D69723]"
            >
              Chat with Restaurant manager
            </button> */}
            <button
              onClick={() => setIsPopupOpen(true)}
              className="w-full max-w-[216px] inline-block rounded-[2px] bg-[#4CAF50] text-[18px] text-white font-semibold 2xl:p-[14px] 2xl:px-[18px] p-3 green-shadow hover:bg-[#209b25] leading-[23px]"
            >
              {t("trackOrder").waiterCall}
            </button>
          </div>
        </div>
      </div>

      {/* Modal Popup */}
      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        {reservation?._id ? (
          <div className="w-full max-w-[579px] mx-auto text-center">
            <div className="flex justify-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h2 className="text-[20px] font-medium mb-[20px] leading-[23px] text-black">
              {t("trackOrder").waiterRequestSent}
              {order?.reservationId?.tableNo}.
              <br />
              {t("trackOrder").waitersent}
            </h2>

            <button
              onClick={() => setIsPopupOpen(false)}
              className="w-full 2xl:max-w-[136px] max-w-[100px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white font-bold 2xl:p-[14px] p-3 green-shadow hover:bg-[#209b25] mt-[30px]"
            >
              {t("trackOrder").ok}
            </button>
          </div>
        ) : (
          <div className="w-full max-w-[579px] mx-auto text-center">
            <h2 className="text-[20px] text-center font-medium mb-[20px] leading-[23px] text-black">
              {t("trackOrder").enterTableNumber}
            </h2>
            <input
              type="text"
              value={tableNo}
              onChange={(e) => setTableNo(e.target.value)}
              placeholder={t("trackOrder").tableNumberPlaceholder}
              className="w-full max-w-[384px] mx-auto py-[13px] px-[16px] border border-[#DDDDDD] rounded-[10px] text-[18px] placeholder:text-[#8C8C8C] focus:outline-none focus:border-[#4CAF50]"
            />
            <button
              onClick={handleWaiterRequest}
              className="w-full 2xl:max-w-[136px] max-w-[100px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold 2xl:p-[14px] p-3 green-shadow hover:bg-[#209b25] mt-[30px]"
            >
              {t("trackOrder").submit}
            </button>
          </div>
        )}
      </PopupModal>

      {/* Chat pop up */}
      <PopupModal2
        isOpen={isChatPopupOpen}
        onClose={() => setIsChatPopupOpen(false)}
      >
        <Chat ownerId={order?.reservationId?.restaurantId?.ownerId}></Chat>
      </PopupModal2>
    </>
  );
};

export default TrackOrder;
